// src/components/roads/RoadMapLayerControls.js
// --- UPDATE (GIS map — road segment health layer) -----------------------------
// Toolbar above the Road Works GIS map:
//   • Display: Roads (whole roads, condition / AI heatmap colours) | Segment health (each segment
//     coloured by PCI).
//   • Switches: map markers, AI detections (dash-cam anomalies), PCI labels.
//   • Health band chips (click to show / hide a band) with live counts.
//   • Network numbers for the shown roads: segments, average PCI, poor, critical, km.
//   • "Slice N roads" for mapped roads that have no segments yet; refresh; load / error state.
// --- END UPDATE ---------------------------------------------------------------
// --- UPDATE (GIS map — whole network) ----------------------------------------
//   • Show: Whole network (every mapped road in the system) | Inventory page (the roads on the
//     current inventory page, with its filters). Network numbers: roads, sliced roads, segments.
//   • "Fit network" zooms the map to every road that is shown.
// --- END UPDATE ---------------------------------------------------------------
// --- UPDATE (roads v1.4 — road ponding forecast)
//   • "Rain water" switch shows / hides the ponding layer; a chip with the forecast (segments with water,
//     deepest, when it starts) — click it to jump to the forecast card.
// --- END UPDATE ---------------------------------------------------------------
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Flex, HStack, Text, Button, ButtonGroup, Switch, FormControl, FormLabel, Tooltip, Badge, Icon, IconButton,
    Spinner, useColorModeValue, Wrap, WrapItem,
} from '@chakra-ui/react';
import { MdRefresh, MdTimeline, MdRoute, MdContentCut, MdPublic, MdViewList, MdZoomOutMap, MdWaterDrop } from 'react-icons/md';
import { SEGMENT_BANDS } from './roadSegmentHealth';

const Toggle = ({ id, label, isChecked, onChange, hint, isDisabled }) => (
    <Tooltip hasArrow label={hint} isDisabled={!hint}>
        <FormControl display="flex" alignItems="center" w="auto" isDisabled={isDisabled}>
            <Switch id={id} size="sm" isChecked={isChecked} onChange={(e) => onChange(e.target.checked)} colorScheme="blue" mr={1.5} />
            <FormLabel htmlFor={id} mb={0} fontSize="xs" fontWeight="600" cursor="pointer">{label}</FormLabel>
        </FormControl>
    </Tooltip>
);
Toggle.propTypes = { id: PropTypes.string.isRequired, label: PropTypes.string.isRequired, isChecked: PropTypes.bool, onChange: PropTypes.func.isRequired, hint: PropTypes.string, isDisabled: PropTypes.bool };

function RoadMapLayerControls({
    mode, onModeChange,
    showMarkers, onShowMarkersChange,
    showAnomalies, onShowAnomaliesChange,
    showLabels, onShowLabelsChange,
    visibleBands, onToggleBand, onShowAllBands,
    summary, loading, error, warnings, unsegmentedCount, onSliceUnsegmented, slicing, onRefresh, isDrawing,
    // --- UPDATE (GIS map — whole network)
    scope = 'network', onScopeChange, networkSummary, onFitNetwork,
    // --- END UPDATE
    // --- UPDATE (roads v1.4)
    showPonding, onShowPondingChange, pondingSummary, onOpenPonding,
    // --- END UPDATE
}) {
    const bg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const chipOffBg = useColorModeValue('gray.100', 'whiteAlpha.100');
    const segments = mode === 'segments';
    const allOn = !visibleBands || SEGMENT_BANDS.every((b) => visibleBands.has(b.key));

    return (
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" px={4} py={3} boxShadow="sm" data-testid="road-map-controls">
            <Flex gap={4} align="center" wrap="wrap">
                <HStack spacing={2}>
                    <Text fontSize="xs" fontWeight="800" color={muted} textTransform="uppercase" letterSpacing="wider">Display</Text>
                    <ButtonGroup size="xs" isAttached variant="outline" isDisabled={isDrawing}>
                        <Button leftIcon={<Icon as={MdRoute} />} onClick={() => onModeChange('roads')} variant={!segments ? 'solid' : 'outline'} colorScheme={!segments ? 'blue' : 'gray'} aria-pressed={!segments}>
                            Roads
                        </Button>
                        <Button leftIcon={<Icon as={MdTimeline} />} onClick={() => onModeChange('segments')} variant={segments ? 'solid' : 'outline'} colorScheme={segments ? 'blue' : 'gray'} aria-pressed={segments}>
                            Segment health
                        </Button>
                    </ButtonGroup>
                </HStack>
                {/* --- UPDATE (GIS map — whole network) */}
                {onScopeChange && (
                    <HStack spacing={2}>
                        <Text fontSize="xs" fontWeight="800" color={muted} textTransform="uppercase" letterSpacing="wider">Show</Text>
                        <ButtonGroup size="xs" isAttached variant="outline" isDisabled={isDrawing}>
                            <Tooltip hasArrow label="Every mapped road in the system">
                                <Button leftIcon={<Icon as={MdPublic} />} onClick={() => onScopeChange('network')} variant={scope === 'network' ? 'solid' : 'outline'} colorScheme={scope === 'network' ? 'purple' : 'gray'} aria-pressed={scope === 'network'}>
                                    Whole network
                                </Button>
                            </Tooltip>
                            <Tooltip hasArrow label="Only the roads on the current inventory page (with its filters)">
                                <Button leftIcon={<Icon as={MdViewList} />} onClick={() => onScopeChange('page')} variant={scope === 'page' ? 'solid' : 'outline'} colorScheme={scope === 'page' ? 'purple' : 'gray'} aria-pressed={scope === 'page'}>
                                    Inventory page
                                </Button>
                            </Tooltip>
                        </ButtonGroup>
                    </HStack>
                )}
                {/* --- END UPDATE */}
                <Toggle id="map-markers" label="Markers" isChecked={showMarkers} onChange={onShowMarkersChange} hint="Road / asset pins and sensor markers" />
                <Toggle id="map-anomalies" label="AI detections" isChecked={showAnomalies} onChange={onShowAnomaliesChange} hint="Dash-cam AI pothole / damage detections" />
                <Toggle id="map-labels" label="PCI labels" isChecked={showLabels} onChange={onShowLabelsChange} isDisabled={!segments}
                    hint="PCI badge on every segment (always shown from zoom 16, up to 300 segments)" />
                {/* --- UPDATE (roads v1.4) */}
                {onShowPondingChange && (
                    <Toggle id="map-ponding" label="Rain water" isChecked={!!showPonding} onChange={onShowPondingChange}
                        hint="Where the live rainfall forecast will leave standing water on the roads (drainage outlook × TessaDEM)" />
                )}
                {pondingSummary && (
                    <Tooltip hasArrow label={pondingSummary.count ? `Forecast: ${pondingSummary.count} segment(s) on ${pondingSummary.roads} road(s) hold water — click for details` : 'No standing water forecast on the mapped roads'}>
                        <Button size="xs" variant="outline" borderRadius="full" colorScheme={pondingSummary.count ? pondingSummary.scheme || 'blue' : 'green'} leftIcon={<Icon as={MdWaterDrop} />}
                            onClick={onOpenPonding} data-testid="ponding-chip">
                            {pondingSummary.count ? `${pondingSummary.count} wet · ${pondingSummary.maxDepth}${pondingSummary.starts ? ` · ${pondingSummary.starts}` : ''}` : 'No ponding'}
                        </Button>
                    </Tooltip>
                )}
                {/* --- END UPDATE */}
                <HStack ml="auto" spacing={2}>
                    {(segments || scope === 'network') && loading && <Spinner size="xs" />}
                    {/* --- UPDATE (GIS map — whole network) */}
                    {networkSummary && (
                        <Text fontSize="xs" color={muted} data-testid="network-summary">
                            <b>{networkSummary.roads}</b> road{networkSummary.roads === 1 ? '' : 's'}{networkSummary.segmented_roads !== undefined ? ` (${networkSummary.segmented_roads} sliced)` : ''}
                        </Text>
                    )}
                    {onFitNetwork && (
                        <Tooltip hasArrow label="Zoom to every road shown">
                            <IconButton size="xs" variant="ghost" icon={<Icon as={MdZoomOutMap} />} onClick={onFitNetwork} aria-label="Fit network" />
                        </Tooltip>
                    )}
                    {/* --- END UPDATE */}
                    {segments && summary && (
                        <Text fontSize="xs" color={muted} data-testid="segment-summary">
                            {summary.total} segments{summary.lengthKm ? ` · ${summary.lengthKm} km` : ''} · avg PCI <b>{summary.avgPci ?? '—'}</b>
                            {summary.poor ? <> · <Text as="span" color="orange.400" fontWeight="700">{summary.poor} poor</Text></> : null}
                            {summary.critical ? <> · <Text as="span" color="red.400" fontWeight="700">{summary.critical} critical</Text></> : null}
                        </Text>
                    )}
                    {segments && unsegmentedCount > 0 && (
                        <Tooltip hasArrow label="These roads are drawn but not cut into segments yet. Slicing creates ~100 m segments so they can be scored.">
                            <Button size="xs" variant="outline" colorScheme="orange" leftIcon={<Icon as={MdContentCut} />} onClick={onSliceUnsegmented} isLoading={slicing} loadingText="Slicing…">
                                Slice {unsegmentedCount} road{unsegmentedCount === 1 ? '' : 's'}
                            </Button>
                        </Tooltip>
                    )}
                    {segments && (
                        <Tooltip hasArrow label="Reload segment health">
                            <IconButton size="xs" variant="ghost" icon={<Icon as={MdRefresh} />} onClick={onRefresh} aria-label="Reload segment health" isDisabled={loading} />
                        </Tooltip>
                    )}
                </HStack>
            </Flex>

            {segments && (
                <Wrap spacing={1.5} mt={2.5} align="center">
                    <WrapItem><Text fontSize="2xs" fontWeight="700" color={muted} mr={1}>HEALTH</Text></WrapItem>
                    {SEGMENT_BANDS.map((b) => {
                        const on = !visibleBands || visibleBands.has(b.key);
                        return (
                            <WrapItem key={b.key}>
                                <Button size="xs" variant="outline" borderRadius="full" borderColor={on ? b.color : border} bg={on ? `${b.color}22` : chipOffBg}
                                    opacity={on ? 1 : 0.55} onClick={() => onToggleBand(b.key)} aria-pressed={on} data-testid={`band-${b.key}`}
                                    leftIcon={<Box as="span" w="10px" h="10px" borderRadius="full" bg={b.color} display="inline-block" />}>
                                    {b.label} <Text as="span" ml={1} color={muted}>{b.range}</Text>
                                    <Badge ml={1.5} fontSize="2xs" variant="subtle" colorScheme={b.scheme}>{summary?.counts?.[b.key] ?? 0}</Badge>
                                </Button>
                            </WrapItem>
                        );
                    })}
                    {!allOn && <WrapItem><Button size="xs" variant="link" onClick={onShowAllBands}>Show all</Button></WrapItem>}
                </Wrap>
            )}
            {segments && error && <Text fontSize="xs" color="red.400" mt={2}>Segments could not be loaded: {error}</Text>}
            {segments && !error && warnings?.length > 0 && <Text fontSize="2xs" color={muted} mt={1.5}>Note: {warnings.join('; ')}.</Text>}
            {isDrawing && <Text fontSize="2xs" color="blue.400" mt={1.5}>Segment colours are paused while a road is being drawn.</Text>}
        </Box>
    );
}

RoadMapLayerControls.propTypes = {
    mode: PropTypes.oneOf(['roads', 'segments']).isRequired,
    onModeChange: PropTypes.func.isRequired,
    showMarkers: PropTypes.bool, onShowMarkersChange: PropTypes.func.isRequired,
    showAnomalies: PropTypes.bool, onShowAnomaliesChange: PropTypes.func.isRequired,
    showLabels: PropTypes.bool, onShowLabelsChange: PropTypes.func.isRequired,
    visibleBands: PropTypes.instanceOf(Set), onToggleBand: PropTypes.func.isRequired, onShowAllBands: PropTypes.func.isRequired,
    summary: PropTypes.object, loading: PropTypes.bool, error: PropTypes.string, warnings: PropTypes.array,
    unsegmentedCount: PropTypes.number, onSliceUnsegmented: PropTypes.func, slicing: PropTypes.bool, onRefresh: PropTypes.func, isDrawing: PropTypes.bool,
    // --- UPDATE (GIS map — whole network)
    scope: PropTypes.oneOf(['network', 'page']), onScopeChange: PropTypes.func, networkSummary: PropTypes.object, onFitNetwork: PropTypes.func,
    // --- END UPDATE
    // --- UPDATE (roads v1.4)
    showPonding: PropTypes.bool, onShowPondingChange: PropTypes.func, pondingSummary: PropTypes.object, onOpenPonding: PropTypes.func,
    // --- END UPDATE
};

export default memo(RoadMapLayerControls);
